import React, { useState } from "react";
import axios from "axios";
import LegalPage from "../../components/Kids/LegalPage";
import Seo from "../../components/Seo";
import SEO from "../../seo/pages.json";

const API = process.env.REACT_APP_API_URL;

export default function KidsDeleteAccountRequest() {
  const [step, setStep] = useState("email"); // email | otp | done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/api/kids/parent/delete-account-request`, { email: email.trim() });
      setStep("otp");
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/api/kids/parent/delete-account-confirm`, { email: email.trim(), otp: otp.trim() });
      setStep("done");
    } catch (err) {
      setError(err.response?.data?.message || "That code is invalid or has expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo
        {...SEO["/delete-account-request"]}
        path="/delete-account-request"
      />
      <LegalPage title="Delete Your Account">
        <p>
          You can permanently delete your Cabil Kids parent account and all associated data from
          here — even if you don't have the app installed or can't sign in.
        </p>

        <h2>What gets deleted</h2>
        <ul>
          <li>Your parent account (name, email, password)</li>
          <li>All child profiles linked to your account, including their game progress, XP, coins, and streaks</li>
          <li>Your subscription and billing history</li>
          <li>Any saved watch-time settings and approved video lists</li>
        </ul>
        <p>This action is permanent and cannot be undone.</p>

        <h2>Request deletion</h2>

        {step === "email" && (
          <form onSubmit={requestCode} className="not-prose space-y-3 max-w-sm">
            <label className="block text-sm font-semibold text-ink">Account email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border border-cardline px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand/40"
            />
            {error && <p className="text-sm text-danger font-semibold">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-brand text-white font-display font-semibold px-6 py-2.5 disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send confirmation code"}
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={confirmDelete} className="not-prose space-y-3 max-w-sm">
            <p className="text-sm text-muted">
              We've sent a 6-digit code to <strong>{email}</strong>. Enter it below to permanently
              delete your account.
            </p>
            <label className="block text-sm font-semibold text-ink">Confirmation code</label>
            <input
              type="text"
              required
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="123456"
              className="w-full rounded-xl border border-cardline px-4 py-2.5 text-[15px] tracking-[6px] focus:outline-none focus:ring-2 focus:ring-brand/40"
            />
            {error && <p className="text-sm text-danger font-semibold">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-danger text-white font-display font-semibold px-6 py-2.5 disabled:opacity-60"
            >
              {busy ? "Deleting…" : "Permanently delete my account"}
            </button>
            <button
              type="button"
              onClick={() => { setStep("email"); setOtp(""); setError(""); }}
              className="block text-sm font-semibold text-muted hover:text-brand"
            >
              Use a different email
            </button>
          </form>
        )}

        {step === "done" && (
          <div className="not-prose rounded-2xl bg-[#DFF5E6] text-[#22874E] font-semibold px-5 py-4 max-w-sm">
            ✓ Your account and all associated data have been permanently deleted.
          </div>
        )}
      </LegalPage>
    </>
  );
}
